import { useCallback } from "react";
import { postEvent, useSnackbarContext } from "@WahooFitness/wahoo-offline-mfe";
import { t } from "@lingui/macro";
import useUserAccountSettings from "@/hooks/useUserAccountSettings";
import { useSettingsVersionedFeatures } from "@/hooks/useSettingsVersionedFeatures";
import { recordRecoverySourceUpdateError } from "./recoverySourceDebugStore";
import {
  clearPendingRecoverySourceAutoSelect,
  readPendingRecoverySourceAutoSelect,
  writePendingRecoverySourceAutoSelect,
} from "./recoverySourceAutoSelectPending";
import { RECOVERY_SOURCE_CHANGED_EVENT } from "./useRecoverySourceToggle";
import useRecoveryHealthScopesRequest from "./useRecoveryHealthScopesRequest";

export type AuthorizedRecoveryCandidate = {
  fitnessAppId: number;
  name: string;
  nativeHealth?: boolean;
};

// Shared across hook instances: more than one list can be mounted, and both would otherwise
// read the same marker before either had cleared it.
let settling = false;

// The first 24/7-capable app a user authorizes becomes the recovery source without a second
// step. Authorizing happens outside this page (external OAuth browser, native Health sheet), so
// the intent is persisted when the user leaves and settled once the app shows up as authorized.
// An account that already has a source is never touched; connecting more apps only connects.
// Turning a source off or deauthorizing an app drops any pending intent, see
// recoverySourceAutoSelectPending.
const useRecoverySourceAutoSelect = () => {
  const { enabled: recovery247Enabled } = useSettingsVersionedFeatures(
    "RECOVERY_247_MINPROFILE_VERSION"
  );
  const { userAccountSettingsData, updateUserAccountSettings } = useUserAccountSettings();
  const { enqueueSnackbar } = useSnackbarContext();
  const { requestRecoveryHealthScopes } = useRecoveryHealthScopesRequest();

  const settingsLoaded = userAccountSettingsData !== undefined;
  const hasRecoverySource = userAccountSettingsData?.recovery_fitness_app_id != null;

  const markPendingAutoSelect = useCallback(
    (fitnessAppId: number) => {
      if (!recovery247Enabled || !settingsLoaded || hasRecoverySource) {
        return;
      }
      void writePendingRecoverySourceAutoSelect(fitnessAppId);
    },
    [hasRecoverySource, recovery247Enabled, settingsLoaded]
  );

  const settlePendingAutoSelect = useCallback(
    async (authorized: AuthorizedRecoveryCandidate[]) => {
      if (settling) {
        return;
      }
      settling = true;
      try {
        const pending = await readPendingRecoverySourceAutoSelect();
        if (!pending) {
          return;
        }
        if (!recovery247Enabled) {
          await clearPendingRecoverySourceAutoSelect();
          return;
        }
        const match = authorized.find((app) => app.fitnessAppId === pending.fitnessAppId);
        // Not authorized yet (OAuth still in flight) or nothing to compare against: keep the
        // marker for a later pass.
        if (!match || !settingsLoaded) {
          return;
        }
        await clearPendingRecoverySourceAutoSelect();
        if (hasRecoverySource) {
          return;
        }
        try {
          // Same as the toggle: Apple Health's connect may have granted export only, so ask for
          // the recovery read scopes first. Nothing left to ask means no sheet.
          if (match.nativeHealth) {
            await requestRecoveryHealthScopes();
          }
          await updateUserAccountSettings({ recovery_fitness_app_id: match.fitnessAppId });
          // Same contract as the toggle: string values only, see useRecoverySourceToggle.
          postEvent(RECOVERY_SOURCE_CHANGED_EVENT, { fitnessAppId: match.fitnessAppId.toString() });
          enqueueSnackbar({
            message: match.nativeHealth
              ? t`Your HRV data source has been changed to ${String(match.name)}.`
              : t`Your recovery source has been changed to ${String(match.name)}.`,
            severity: "success",
          });
        } catch (error) {
          recordRecoverySourceUpdateError(match.fitnessAppId, error);
          enqueueSnackbar({
            message: match.nativeHealth
              ? t`An error occurred while updating your HRV data source. Please try again later.`
              : t`An error occurred while updating your recovery source. Please try again later.`,
            severity: "error",
          });
        }
      } finally {
        settling = false;
      }
    },
    [
      enqueueSnackbar,
      hasRecoverySource,
      recovery247Enabled,
      requestRecoveryHealthScopes,
      settingsLoaded,
      updateUserAccountSettings,
    ]
  );

  return { markPendingAutoSelect, settlePendingAutoSelect };
};

export default useRecoverySourceAutoSelect;
